/**
 * The order bar is rendered on every page by a different module from the one that pulls the ecommerce
 * order into the cart, so it cannot tell an empty cart from a cart the pull has not reached yet. Both
 * render as zero, and the wrong one of the two is the confident one.
 *
 * The pull publishes itself here as a shared MobX observable, and consumers react to it directly
 * instead of guessing from the cart alone. This is a plain singleton rather than a data action because
 * the bar is a component, not a module - it only receives props its parent module passes down, so it
 * cannot be given a data action of its own.
 */
import { action, observable } from 'mobx';

class EcomCartLoadState {
    @observable public isLoading: boolean = false;

    @action
    public setLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
    }
}

export const ecomCartLoadState = new EcomCartLoadState();

/**
 * Runs the pull and reports it as in flight for exactly as long as it runs - including however many
 * attempts it takes internally to get a real answer. Every way out - success, failure, or finding
 * nothing to pull - ends it, because a bar left waiting on a pull that already finished is a worse
 * failure than the zero it was showing.
 */
export const whileEcomCartLoads = async <T>(pull: () => Promise<T>): Promise<T> => {
    ecomCartLoadState.setLoading(true);

    try {
        return await pull();
    } finally {
        ecomCartLoadState.setLoading(false);
    }
};
